import {
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  LinearProgress,
  Switch,
  Textarea,
} from '@mui/joy';

import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { CopyIconButton } from '@/components/CopyButton/CopyButton';
import { DeleteConfirmationButton } from '@/components/Delete';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { useUserSSOInfo } from '@/hooks/useAuth/useAuth';
import {
  useCreateSSOSettingsMutation,
  useCurrentOrganization,
  useDeleteSSOSettingsMutation,
  useOrgWorkspaces,
  useSSOSettings,
  useSetSSOOnly,
  useUpdateSSOSettingsMutation,
} from '@/hooks/useSwr';
import {
  SSOSettingsCreate,
  SSOSettingsUpdate,
  TenantSchema,
} from '@/types/schema';

import { RoleSelect, WorkspaceSelect } from './components/InviteOrgMemberModal';

interface FormValues {
  metadata_url: string;
  metadata_xml: string;
  default_workspace_role_id: string;
  default_workspace_ids: string[];
}

const SSOSettingsForm: React.FC = () => {
  'use no memo';
  const { data, error, isLoading } = useSSOSettings();
  const createSSOSettings = useCreateSSOSettingsMutation();
  const updateSSOSettings = useUpdateSSOSettingsMutation();
  const deleteSSOSettings = useDeleteSSOSettingsMutation();

  const setSSOOnly = useSetSSOOnly();

  const { data: workspaces } = useOrgWorkspaces();
  const { data: org } = useCurrentOrganization();
  const { isSSOLogin } = useUserSSOInfo();

  const [mode, setMode] = useState<'url' | 'xml'>('url');

  const [ssoOnly, setSsoOnly] = useState(org?.sso_only || false);

  const isConfigured = data && data.length > 0;

  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      metadata_url: '',
      metadata_xml: '',
      default_workspace_role_id: '',
      default_workspace_ids: [],
    },
  });

  const onSubmit = async (formData: FormValues) => {
    if (isConfigured) {
      const ssoSettings: SSOSettingsUpdate = {
        default_workspace_ids: formData.default_workspace_ids,
        default_workspace_role_id: formData.default_workspace_role_id,
        id: data?.[0]?.id,
      };
      await updateSSOSettings.trigger({
        json: ssoSettings,
        templateUrlParams: {
          id: data[0].id,
        },
      });
      await setSSOOnly.trigger({
        json: { sso_only: ssoOnly },
      });
    } else {
      const data: SSOSettingsCreate = {
        default_workspace_ids: formData.default_workspace_ids,
        default_workspace_role_id: formData.default_workspace_role_id,
        ...(mode === 'url'
          ? { metadata_url: formData.metadata_url }
          : { metadata_xml: formData.metadata_xml }),
      };

      await createSSOSettings.trigger({ json: data });
      await setSSOOnly.trigger({
        json: { sso_only: ssoOnly },
      });
    }
  };

  const onDelete = async (id: string) => {
    if (data && data.length > 0) {
      await deleteSSOSettings.trigger({
        templateUrlParams: {
          id: id,
        },
      });
      reset({
        metadata_url: '',
        metadata_xml: '',
        default_workspace_ids: [],
        default_workspace_role_id: '',
      });
    }
  };

  useEffect(() => {
    if (data && data.length > 0) {
      reset(data[0]);
      setMode(data[0].metadata_xml ? 'xml' : 'url');
    }
    if (org) {
      setSsoOnly(org.sso_only || false);
    }
  }, [data, reset, workspaces, org]);

  if (error) {
    return <div>Error loading SSO settings</div>;
  }

  if (isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  const rootUrl = new URL('/', window.location.href).origin;
  const mutateError =
    createSSOSettings.error || updateSSOSettings.error || setSSOOnly.error;
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-[500px] space-y-4">
      {isConfigured && (
        <div className="relative mb-4 rounded-md bg-success-primary p-4">
          <p className="text-success">
            SSO login is available for your organization
          </p>
          {org?.sso_login_slug && (
            <>
              <p className="text-success">
                <Link
                  className="underline"
                  to={`/sso/login/${org?.sso_login_slug}`}
                >
                  {rootUrl}/sso/login/{org?.sso_login_slug}
                </Link>
              </p>
              <p className="text-success">
                Please share this URL with your members.
              </p>
              <CopyIconButton
                copy={`${rootUrl}/sso/login/${org?.sso_login_slug}`}
                className="absolute right-2 top-2 text-success"
                copyText="Copy SSO login URL"
              />
            </>
          )}
        </div>
      )}
      {!isConfigured && (
        <FormControl className="">
          <FormLabel>SAML metadata type</FormLabel>
          <Switch
            checked={mode === 'xml'}
            onChange={(e) => setMode(e.target.checked ? 'xml' : 'url')}
            startDecorator={'URL'}
            endDecorator={'XML'}
            sx={{ alignSelf: 'flex-start' }}
          />
        </FormControl>
      )}
      {mode === 'url' && (
        <Controller
          name="metadata_url"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              <FormLabel htmlFor="metadata_url">SAML metadata URL</FormLabel>
              <Input
                id="metadata_url"
                type="text"
                disabled={isConfigured}
                {...field}
                placeholder="https://domain.com/sso/saml/metadata"
              />
              <FormHelperText>
                {fieldState.error
                  ? 'SAML metadata URL is required'
                  : isConfigured
                  ? 'Delete the current configuration to change the SAML metadata URL'
                  : 'Enter the SAML endpoint URL for authentication.'}
              </FormHelperText>
            </FormControl>
          )}
        />
      )}
      {mode === 'xml' && (
        <Controller
          name="metadata_xml"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              <FormLabel htmlFor="metadata_xml">SAML metadata XML</FormLabel>
              <Textarea
                id="metadata_xml"
                minRows={10}
                {...field}
                disabled={isConfigured}
              />
              <FormHelperText>
                {fieldState.error
                  ? 'SAML metadata XML is required'
                  : isConfigured
                  ? 'Delete the current configuration to change the SAML metadata XML'
                  : 'Alternatively enter the SAML metadata XML for authentication.'}
              </FormHelperText>
            </FormControl>
          )}
        />
      )}

      <Controller
        name="default_workspace_role_id"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <FormControl error={!!fieldState.error}>
            <FormLabel>Default workspace role</FormLabel>
            <RoleSelect
              value={field.value}
              onChange={field.onChange}
              groupType="workspace"
            />
            <FormHelperText>
              {fieldState.error && 'Default role is required'}
            </FormHelperText>
          </FormControl>
        )}
      />

      <Controller
        name="default_workspace_ids"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => {
          const mWorkspaces = workspaces?.filter((workspace) =>
            field.value.includes(workspace.id)
          );

          const setField: React.Dispatch<
            React.SetStateAction<TenantSchema[]>
          > = (value) => {
            if (value instanceof Function) {
              const newWorkspaces = value(mWorkspaces);
              field.onChange(newWorkspaces.map((workspace) => workspace.id));
            } else {
              field.onChange(value);
            }
          };

          return (
            <FormControl error={!!fieldState.error}>
              <FormLabel>Default workspaces</FormLabel>
              <WorkspaceSelect value={mWorkspaces} onChange={setField} />
              <FormHelperText>
                {fieldState.error &&
                  'At least one default workspace is required'}
              </FormHelperText>
            </FormControl>
          );
        }}
      />
      <FormControl>
        <Checkbox
          label="Login via SSO only"
          checked={ssoOnly}
          onChange={(e) => setSsoOnly(e.target.checked)}
        />

        <FormHelperText>
          Only allow SSO login for this organization and no other login methods
        </FormHelperText>
      </FormControl>
      {mutateError && <ExpandableErrorAlert error={mutateError} />}
      <div className="flex justify-end gap-3">
        {isConfigured && (
          <DeleteConfirmationButton
            disabled={isSSOLogin}
            deleteButtonText="Delete"
            onDelete={() => onDelete(data[0].id)}
            isDeleting={deleteSSOSettings.isMutating}
            title={isSSOLogin ? '' : 'Delete SAML SSO configuration'}
            description={
              isSSOLogin
                ? 'You cannot delete SSO configuration while logged in via SSO, please log in with your email instead'
                : 'Are you sure you want to delete the SAML SSO configuration? This action cannot be undone.'
            }
            trigger={<Button color="danger">Delete</Button>}
          />
        )}
        <Button
          type="submit"
          variant="solid"
          color="primary"
          loading={
            createSSOSettings.isMutating ||
            updateSSOSettings.isMutating ||
            setSSOOnly.isMutating
          }
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export const OrganizationSSOConfiguration = () => {
  return (
    <div className="p-1">
      <SSOSettingsForm />
    </div>
  );
};
